/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  summary: string;
  status: string;
  thematic_area?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  company_id: string;
  company_name?: string;
  locations?: any[];
  metrics?: any[];
  budget?: number;
  tranche_plan?: any[];
  beneficiary_profile?: string;
  proposal_content?: any;
  project_ngo_status?: string; // Status from project_ngos table
  requested_changes?: string | null; // Change request text
  changes_requested?: any; // From projects.changes_requested
}

const NGOProjects: React.FC = () => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState('');
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  useEffect(() => {
    if (ngoId) {
      loadProjects();
    }
  }, [ngoId]);

  const loadProjects = async () => {
    if (!ngoId) return;

    setLoading(true);
    setError('');

    try {
      // Fetch projects assigned to this NGO with full details
      const { data: projectNgos, error: projectNgosError } = await supabase
        .from('project_ngos')
        .select(`
          project_id,
          status,
          requested_changes,
          projects:project_id (
            id,
            name,
            summary,
            status,
            thematic_area,
            start_date,
            end_date,
            locations,
            metrics,
            budget,
            tranche_plan,
            beneficiary_profile,
            proposal_content,
            reporting_cadence,
            company_id,
            changes_requested,
            companies:company_id (
              id,
              name
            )
          )
        `)
        .eq('ngo_id', ngoId);

      if (projectNgosError) throw projectNgosError;

      // Get all project IDs to fetch full project data and metrics
      const projectIds = (projectNgos || [])
        .filter((pn: any) => pn.projects !== null && pn.projects !== undefined)
        .map((pn: any) => pn.projects.id);

      // Fetch full project data directly to ensure we get all fields
      let projectsMap: Record<string, any> = {};
      if (projectIds.length > 0) {
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .in('id', projectIds);

        if (!projectsError && projectsData) {
          projectsData.forEach((p: any) => {
            projectsMap[p.id] = p;
          });
        }
      }

      // Fetch metrics from project_metrics table (primary source)
      let metricsMap: Record<string, any[]> = {};
      if (projectIds.length > 0) {
        const { data: metricsData, error: metricsError } = await supabase
          .from('project_metrics')
          .select('project_id, metric_name, target_value, unit')
          .in('project_id', projectIds);

        if (!metricsError && metricsData && metricsData.length > 0) {
          metricsData.forEach((m: any) => {
            if (!metricsMap[m.project_id]) {
              metricsMap[m.project_id] = [];
            }
            metricsMap[m.project_id].push({
              name: m.metric_name,
              target: m.target_value,
              unit: m.unit || ''
            });
          });
        } else {
          const { data: progressMetricsData } = await supabase
            .from('project_metrics_progress')
            .select('project_id, metric_name, target_value, unit')
            .in('project_id', projectIds);

          if (progressMetricsData) {
            progressMetricsData.forEach((m: any) => {
              if (!metricsMap[m.project_id]) {
                metricsMap[m.project_id] = [];
              }
              metricsMap[m.project_id].push({
                name: m.metric_name,
                target: m.target_value,
                unit: m.unit || ''
              });
            });
          }
        }
      }

      const safeProjects: Project[] = (projectNgos || [])
        .filter((pn: any) => pn.projects !== null && pn.projects !== undefined)
        .map((pn: any) => {
          const project = projectsMap[pn.projects.id] || pn.projects;

          let locations = project.locations;
          if (locations) {
            if (typeof locations === 'string') {
              try {
                locations = JSON.parse(locations);
              } catch {
                locations = [];
              }
            }
            if (!Array.isArray(locations)) locations = [];
          } else {
            locations = [];
          }

          const metrics = metricsMap[project.id] || [];

          let tranche_plan = project.tranche_plan;
          if (tranche_plan) {
            if (typeof tranche_plan === 'string') {
              try {
                tranche_plan = JSON.parse(tranche_plan);
              } catch {
                tranche_plan = [];
              }
            }
            if (!Array.isArray(tranche_plan)) tranche_plan = [];
          } else {
            tranche_plan = [];
          }

          let budgetValue = null;
          if (project.budget !== null && project.budget !== undefined) {
            if (typeof project.budget === 'string') {
              budgetValue = parseFloat(project.budget);
            } else {
              budgetValue = parseFloat(project.budget.toString());
            }
            if (isNaN(budgetValue)) budgetValue = null;
          }

          return {
            id: project.id,
            name: project.name,
            summary: project.summary || '',
            status: project.status,
            thematic_area: project.thematic_area || null,
            start_date: project.start_date || null,
            end_date: project.end_date || null,
            locations: locations,
            metrics: metrics,
            budget: budgetValue,
            tranche_plan: tranche_plan,
            beneficiary_profile: project.beneficiary_profile,
            proposal_content: project.proposal_content,
            company_id: project.company_id,
            company_name: project.companies?.name || 'Company',
            project_ngo_status: pn.status,
            requested_changes: pn.requested_changes,
            changes_requested: project.changes_requested
          } as Project;
        });

      setProjects(safeProjects);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: '#6b7280',
      assigned: '#3b82f6',
      accepted: '#10b981',
      pre_start: '#f59e0b',
      active: '#10b981',
      closed: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      assigned: 'Assigned',
      accepted: 'Accepted',
      pre_start: 'Pre-Start',
      active: 'Active',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const projectsWithChangeRequests = projects.filter(p =>
    p.project_ngo_status === 'changes_requested' ||
    p.requested_changes ||
    p.changes_requested
  );

  const assignedProjects = projects.filter(p =>
    p.status === 'assigned' &&
    (!p.thematic_area || p.thematic_area === 'null' || p.thematic_area === '')
  );

  return (
    <div className="projects-page-container">
      <div className="projects-page-header">
        <h1>My Projects</h1>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* New Project Assignment Banner */}
      {assignedProjects.length > 0 && (
        <div className="card" style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          border: 'none',
          marginBottom: '1.5rem',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }}></div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative', zIndex: 1 }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(4px)',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem'
            }}>
              🎉
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: 700 }}>
                New Project Assigned!
              </h2>
              <p style={{ margin: '0.25rem 0 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                You have <strong>{assignedProjects.length}</strong> project{assignedProjects.length !== 1 ? 's' : ''} waiting for setup.
                Please fill in the project details to activate your implementation plan.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alert banner for change requests */}
      {projectsWithChangeRequests.length > 0 && (
        <div className="card" style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          marginBottom: '1.5rem',
          padding: '1rem 1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#92400e', fontSize: '1rem' }}>
                Action Required: {projectsWithChangeRequests.length} Project{projectsWithChangeRequests.length !== 1 ? 's' : ''} with Change Requests
              </strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#92400e', fontSize: '0.875rem' }}>
                The company has requested changes to {projectsWithChangeRequests.length === 1 ? 'a project' : 'some projects'}.
                Please review the change requests and update the project details accordingly.
              </p>
            </div>
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <h2 style={{ marginBottom: '1rem' }}>No Projects Assigned</h2>
          <p style={{ color: 'var(--muted)' }}>
            You don't have any projects assigned yet. Projects will appear here once a company assigns them to you.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Card View (< 768px) */}
          <div className="projects-mobile-cards">
            {projects.map((project) => {
              const hasChangeRequest = project.project_ngo_status === 'changes_requested' ||
                project.requested_changes ||
                project.changes_requested;
              const changeRequestText = project.requested_changes ||
                (project.changes_requested
                  ? (typeof project.changes_requested === 'object'
                    ? project.changes_requested.comment
                    : project.changes_requested)
                  : '');

              return (
                <div
                  key={project.id}
                  className="project-card-item"
                  style={hasChangeRequest ? { borderColor: '#f59e0b', backgroundColor: '#fffbeb' } : {}}
                >
                  <div className="project-card-header">
                    <div>
                      <h3 className="project-card-title">{project.name}</h3>
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                        Company: <strong>{project.company_name || 'Company'}</strong>
                      </div>
                    </div>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.65rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: `${getStatusColor(project.status)}15`,
                        color: getStatusColor(project.status),
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {getStatusLabel(project.status)}
                    </span>
                  </div>

                  {hasChangeRequest && (
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      background: '#fef3c7',
                      borderRadius: '8px',
                      border: '1px solid #f59e0b',
                      fontSize: '0.8125rem',
                      color: '#92400e'
                    }}>
                      <strong>⚠️ Changes Requested:</strong> {changeRequestText || 'Details requested by company'}
                    </div>
                  )}

                  {project.summary && (
                    <p className="project-card-summary">{project.summary}</p>
                  )}

                  <div className="project-card-details">
                    <div className="project-card-detail-row">
                      <span className="project-card-detail-label">Thematic Area:</span>
                      <span className="project-card-detail-value">
                        {project.thematic_area && project.thematic_area.trim() !== '' && project.thematic_area !== 'null' ? (
                          String(project.thematic_area).replace(/_/g, ' ')
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>Not filled</span>
                        )}
                      </span>
                    </div>

                    <div className="project-card-detail-row">
                      <span className="project-card-detail-label">Budget:</span>
                      <span className="project-card-detail-value">
                        {project.budget !== null && project.budget !== undefined && !isNaN(Number(project.budget)) && Number(project.budget) > 0 ? (
                          <strong style={{ color: '#10b981' }}>₹{Number(project.budget).toLocaleString('en-IN')}</strong>
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>Not set</span>
                        )}
                      </span>
                    </div>

                    <div className="project-card-detail-row">
                      <span className="project-card-detail-label">Duration:</span>
                      <span className="project-card-detail-value">
                        {project.start_date && project.end_date &&
                          project.start_date !== null && project.end_date !== null &&
                          project.start_date !== 'null' && project.end_date !== 'null' &&
                          project.start_date !== '' && project.end_date !== '' ? (
                          `${new Date(project.start_date).toLocaleDateString('en-IN', { month: 'short' })} - ${new Date(project.end_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>Not set</span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="project-card-actions">
                    {(!project.thematic_area || project.status === 'assigned') ? (
                      <Link
                        to={`/ngo/ngo/projects/${project.id}/details`}
                        className="btn btn-sm btn-primary btn-pulse"
                        style={{ fontSize: '0.8125rem', padding: '0.5rem 1rem', fontWeight: 700, width: '100%' }}
                      >
                        🚀 Activate Project
                      </Link>
                    ) : (
                      <Link
                        to={`/ngo/ngo/projects/${project.id}/details`}
                        className="btn btn-sm btn-outline"
                        style={{ fontSize: '0.8125rem', padding: '0.5rem 1rem', width: '100%' }}
                      >
                        Edit Details
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View (>= 768px) */}
          <div className="table-container projects-desktop-table">
            <table className="table" style={{ minWidth: '950px' }}>
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Company</th>
                  <th>Thematic Area</th>
                  <th>Duration</th>
                  <th>Metrics</th>
                  <th>Budget</th>
                  <th>Locations</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  let proposalData: any = {};
                  if (project.proposal_content) {
                    try {
                      proposalData = typeof project.proposal_content === 'string'
                        ? JSON.parse(project.proposal_content)
                        : project.proposal_content;
                    } catch {
                      proposalData = {};
                    }
                  }

                  const hasChangeRequest = project.project_ngo_status === 'changes_requested' ||
                    project.requested_changes ||
                    project.changes_requested;
                  const changeRequestText = project.requested_changes ||
                    (project.changes_requested
                      ? (typeof project.changes_requested === 'object'
                        ? project.changes_requested.comment
                        : project.changes_requested)
                      : '');

                  return (
                    <tr key={project.id} style={hasChangeRequest ? { backgroundColor: '#fef3c715' } : {}}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <strong>{project.name}</strong>
                              {hasChangeRequest && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    border: '1px solid #f59e0b'
                                  }}
                                  title={changeRequestText || 'Changes requested by company'}
                                >
                                  ⚠️ Changes Requested
                                </span>
                              )}
                            </div>
                            {project.summary && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                                {project.summary.length > 50 ? project.summary.substring(0, 50) + '...' : project.summary}
                              </div>
                            )}
                            {hasChangeRequest && changeRequestText && (
                              <div style={{
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                background: '#fef3c7',
                                borderRadius: '6px',
                                border: '1px solid #f59e0b',
                                fontSize: '0.75rem',
                                color: '#92400e'
                              }}>
                                <strong>Change Request:</strong> {changeRequestText.length > 100
                                  ? changeRequestText.substring(0, 100) + '...'
                                  : changeRequestText}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{project.company_name || 'Company'}</td>
                      <td>
                        {project.thematic_area && project.thematic_area.trim() !== '' && project.thematic_area !== 'null' ? (
                          <span style={{ textTransform: 'capitalize', fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                            {String(project.thematic_area).replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Not filled</span>
                        )}
                      </td>
                      <td>
                        {project.start_date && project.end_date &&
                          project.start_date !== null && project.end_date !== null &&
                          project.start_date !== 'null' && project.end_date !== 'null' &&
                          project.start_date !== '' && project.end_date !== '' ? (
                          <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                            {new Date(project.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(project.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Not set</span>
                        )}
                      </td>
                      <td>
                        {project.metrics && Array.isArray(project.metrics) && project.metrics.length > 0 && project.metrics.some((m: any) => m && (m.name || m.metric)) ? (
                          <div style={{ fontSize: '0.875rem' }}>
                            <strong style={{ color: '#10b981' }}>{project.metrics.length}</strong> metric{project.metrics.length !== 1 ? 's' : ''}
                            <div style={{ marginTop: '0.25rem', color: 'var(--muted)', fontSize: '0.75rem' }}>
                              {project.metrics.filter((m: any) => m && (m.name || m.metric)).slice(0, 2).map((m: any, idx: number) => (
                                <div key={idx} style={{ marginBottom: '0.125rem' }}>
                                  • {m.name || m.metric || 'Metric'}: {m.target || m.target_value || 'N/A'} {m.unit || ''}
                                </div>
                              ))}
                              {project.metrics.length > 2 && (
                                <div style={{ color: '#6b7280', marginTop: '0.125rem' }}>+{project.metrics.length - 2} more</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No metrics</span>
                        )}
                      </td>
                      <td>
                        {project.budget !== null && project.budget !== undefined && !isNaN(Number(project.budget)) && Number(project.budget) > 0 ? (
                          <strong style={{ color: '#10b981', fontSize: '0.875rem' }}>₹{Number(project.budget).toLocaleString('en-IN')}</strong>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Not set</span>
                        )}
                      </td>
                      <td>
                        {project.locations && Array.isArray(project.locations) && project.locations.length > 0 ? (
                          (() => {
                            const validLocations = project.locations.filter((loc: any) =>
                              loc &&
                              (loc.name || loc.state || loc.district) &&
                              loc.name !== '' && loc.name !== 'null'
                            );
                            return validLocations.length > 0 ? (
                              <div style={{ fontSize: '0.875rem' }}>
                                <strong style={{ color: '#10b981' }}>{validLocations.length}</strong> location{validLocations.length !== 1 ? 's' : ''}
                                <div style={{ marginTop: '0.25rem', color: 'var(--muted)', fontSize: '0.75rem' }}>
                                  {validLocations.slice(0, 2).map((loc: any, idx: number) => (
                                    <div key={idx} style={{ marginBottom: '0.125rem' }}>
                                      • {loc.name || 'Location'} {loc.state ? `(${loc.state})` : ''}
                                    </div>
                                  ))}
                                  {validLocations.length > 2 && (
                                    <div style={{ color: '#6b7280', marginTop: '0.125rem' }}>+{validLocations.length - 2} more</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No locations</span>
                            );
                          })()
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No locations</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            backgroundColor: `${getStatusColor(project.status)}15`,
                            color: getStatusColor(project.status)
                          }}
                        >
                          {getStatusLabel(project.status)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {(!project.thematic_area || project.status === 'assigned') ? (
                            <Link
                              to={`/ngo/ngo/projects/${project.id}/details`}
                              className="btn btn-sm btn-primary btn-pulse"
                              style={{ fontSize: '0.75rem', padding: '0.375rem 1rem', fontWeight: 700 }}
                            >
                              🚀 Activate Project
                            </Link>
                          ) : (
                            <Link
                              to={`/ngo/ngo/projects/${project.id}/details`}
                              className="btn btn-sm btn-outline"
                              style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                            >
                              Edit
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default NGOProjects;
